'use strict';

import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { createUpdateApplicant, createSaveApplication, getYtjInformation } from '../../actions/application';
import { createLoginToken } from '../../actions/login';
import { trackEvent } from '../../utils/gtm';
import BankSelectionPanel from '../BankSelectionPanel';
import { buildAttribution, campaignCodeFromUrl } from '../becomeApplicantV2/applicationContext';
import { normalizeBic } from '../becomeApplicantV2/bic';
import { Tick, TrustChips } from '../becomeApplicantV2/Trust';
import { TERMS_URL } from '../becomeApplicantV2/PageFrame';
import {
  AUTH_BYPASS, AUTH_CONTEXT_INVOICE_FUNDING, fmt, validators as baseValidators, ERROR_TEXT as BASE_ERROR_TEXT,
  SAVE_FAILED_PHONE_MSG, resolveSavedApplicationId, focusAndScrollToFieldError,
  Range, KneeRange, Consent, Field, Assurance, StepsBar, AttachmentsBlock, AuthButton, StickyCta, SuccessIcon, ArrowIcon, LockIcon,
  CompanyContactFields, CampaignCode, useProcessStarted, useStickyVisible, useAttachments, useBusinessId,
} from '../becomeApplicantV2/pieces';
import { VOLUME, VOLUME_KNEE, TERMS, formatTerms, calculateFactoring, BILLING_SYSTEMS } from './invoiceContext';

/**
 * Laskurahoitus (invoice financing), two steps — same structure as the loan form but
 * with the invoice financing calculator, a business description, two separate consents
 * (automated credit decision; terms + credit check) and the billing system question.
 *
 * Differences to the loan flow that come from the live invoiceFinancing flow:
 *  - pricing: 0,85 % / 1,75 % / 2,5 % / 3,45 % of monthly volume by payment term (≤14 / ≤30 / ≤45 / over 45 days)
 *  - loanApplicationLoanTypeEnum 5, invoiceVolume, paymentTerms, loanPurposeDescription,
 *    factoringSourceEnum are posted; the monthly invoicing volume is also sent as loanAmount
 *    (no fixed funding limit — each invoice is assessed separately, see API PR 1011)
 *  - GTM process name "invoice_funding_self"
 */

const PROCESS_NAME = 'invoice_funding_self';
const CHIPS = ['Ilman vakuuksia', 'Päätös samana päivänä', '100 % suomalainen'];

const validators = { ...baseValidators, description: (v) => v.trim().length >= 3 };
const ERROR_TEXT = {
  ...BASE_ERROR_TEXT,
  description: 'Liiketoiminnan kuvaus on pakollinen tieto.',
  aiConsent: 'Hyväksy automatisoidun päätöksenteon käyttö jatkaaksesi.',
};

function FinSliders({ fin, onChange }) {
  return (
    <>
      <KneeRange label="Kuukausittain rahoitettava myyntilaskutus" value={fin.volume} range={VOLUME} knee={VOLUME_KNEE} unit="€" big format={fmt} ends={['5 000 €', '500 000 €']} onChange={(v) => onChange({ volume: v })} />
      <Range label="Laskujen keskimääräinen maksuaika" value={fin.terms} range={TERMS} unit="pv" format={formatTerms} ends={['0-14 pv', 'yli 45 pv']} onChange={(v) => onChange({ terms: v })} />
    </>
  );
}

function InvoiceFinancingV2Process({ context, application, updateApplication, fetchYtj, saveApplication, requestLoginToken, onStepChange }) {
  const prefilled = context.mode === 'prefilled';

  const [step, setStep] = useState(1); // 1 | 2 | 'done'
  // The page frame hides the cross-sell text under the form once the user has left step 1.
  useEffect(() => { if (onStepChange) onStepChange(step); }, [step, onStepChange]);
  const [fin, setFin] = useState(context.fin);
  const urlCode = campaignCodeFromUrl(context.values); // ?koodi=… prefills the campaign code
  const [fields, setFields] = useState({ bic: '', companyName: '', phone: '', email: '', description: '', discountCode: urlCode });
  const [touched, setTouched] = useState({});
  const [aiConsent, setAiConsent] = useState(false);
  const [terms, setTerms] = useState(false);
  const [billingSystem, setBillingSystem] = useState(0);
  const [showCampaign, setShowCampaign] = useState(Boolean(urlCode));
  const [showAttachments, setShowAttachments] = useState(false);
  const [editing, setEditing] = useState(!prefilled); // no URL parameters → sliders open from the start
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [loadingServiceId, setLoadingServiceId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const attribution = useRef(null);
  const cardRef = useRef(null);
  const completed = useRef(false);
  const markStarted = useProcessStarted(PROCESS_NAME);
  const stickyVisible = useStickyVisible(cardRef, step);
  const { attachments, add: addFiles, remove: removeFile } = useAttachments(setSubmitError);
  const bic = useBusinessId({ fields, setFields, setErrors, application, updateApplication, fetchYtj });

  const calc = calculateFactoring(fin);

  // Mount: resolve marketing attribution once and seed Redux (no offer API call for invoice financing).
  useEffect(() => {
    attribution.current = buildAttribution(context.values);
    updateApplication({ invoiceVolume: fin.volume, paymentTerms: fin.terms, loanApplicationLoanTypeEnum: 5, ...attribution.current });
  }, []); // mount only

  const changeFin = (patch) => { markStarted(); setFin((f) => ({ ...f, ...patch })); };
  const setField = (name) => (e) => {
    markStarted();
    const value = name === 'bic' ? bic.onChange(e) : e.target.value;
    setFields((f) => ({ ...f, [name]: value }));
    if (errors[name] && (!validators[name] || validators[name](value))) setErrors((er) => ({ ...er, [name]: undefined }));
  };
  const blurField = (name) => () => {
    setTouched((t) => ({ ...t, [name]: true }));
    if (name === 'bic') bic.onBlurNormalize();
    if (fields[name].trim() !== '' && validators[name] && !validators[name](fields[name])) {
      setErrors((er) => ({ ...er, [name]: ERROR_TEXT[name] }));
      trackEvent('field_error', { field_id: name, step: 1 });
    }
  };
  const isValid = (name) => touched[name] && validators[name] && validators[name](fields[name]);

  // The API has only POST: a body without an id creates a new application, a body with the
  // id of an earlier save updates it. The live flow resends the id via Redux state; do the
  // same here so the step-1 save, the pre-authentication save and any back-and-forth all
  // land on one application instead of several.
  const buildPayload = () => ({
    ...(savedId ? { id: savedId } : {}),
    invoiceVolume: fin.volume,
    paymentTerms: fin.terms,
    loanAmount: fin.volume, // expected monthly invoicing volume; there is no fixed funding limit (API PR 1011)
    capitalFreeMonths: 0,
    LoanPurposeTypeEnum: 0,
    LoanGuaranteeTypeEnum: 0,
    LoanDueDateTypeEnum: 0,
    freeMonthsToPaymentsCheck: false,
    ApplicationSource: '',
    loanApplicationTypeEnum: 0,
    loanApplicationLoanTypeEnum: 5, // invoice financing
    factoringSourceEnum: billingSystem,
    factoringSourceDescription: '',
    loanPurposeDescription: fields.description.trim(),
    bic: normalizeBic(fields.bic),
    companyName: fields.companyName.trim(),
    companySsnBic: application.companySsnBic,
    businessLine: application.businessLine,
    address: application.address,
    addressCo: application.addressCo,
    zip: application.zip,
    city: application.city,
    phone: fields.phone.trim(),
    email: fields.email.trim(),
    discountCode: fields.discountCode.trim(),
    monthlyPayment: 0,
    ...(attribution.current || {}),
  });

  const validateStep1 = () => {
    const next = {};
    Object.keys(validators).forEach((k) => { if (!validators[k](fields[k])) next[k] = ERROR_TEXT[k]; });
    if (!aiConsent) next.aiConsent = ERROR_TEXT.aiConsent;
    if (!terms) next.terms = ERROR_TEXT.terms;
    setErrors(next);
    setTouched({ bic: true, companyName: true, phone: true, email: true, description: true });
    const firstBad = Object.keys(next)[0];
    if (firstBad) {
      trackEvent('form_submit_attempt', { step: 1, ok: false });
      focusAndScrollToFieldError(firstBad);
    }
    return !firstBad;
  };

  const scrollToCard = () => cardRef.current && cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Step 1 → 2. The live flow saves the application here already; keep that.
  const goStep2 = async () => {
    if (busy || !validateStep1()) return;
    setBusy(true);
    setSubmitError(null);
    const payload = buildPayload();
    updateApplication(payload);
    try {
      const saved = await saveApplication(payload);
      setSavedId(saved.id);
      if (saved.invoiceVolume > 0 && !completed.current) {
        completed.current = true; // going back and forward again must not report a second conversion
        trackEvent('kr_process_completed', { process_name: PROCESS_NAME, value: saved.invoiceVolume, application_id: saved.id });
      }
      localStorage.removeItem(`kr_process_started_${PROCESS_NAME}`);
    } catch (e) {
      trackEvent('kr_process_error', { process_name: PROCESS_NAME, error_message: e.message || 'save after step 1 failed' });
    } finally {
      setBusy(false);
      setStep(2);
      trackEvent('step_complete', { step: 1 });
      setTimeout(scrollToCard, 0);
    }
  };

  // Step 2: save with billing system + attachments, then hand over to the identity provider.
  // H1 mitigation (investigation 5.10.2026): merge Redux application (stage-1 API
  // response) so a partial update does not null out server-set fields.
  const authenticate = async (serviceId) => {
    if (busy) return;
    setBusy(true);
    setLoadingServiceId(serviceId);
    setSubmitError(null);
    let id = savedId;
    try {
      const saved = await saveApplication({ ...application, ...buildPayload(), attachments });
      id = resolveSavedApplicationId(saved, savedId);
      if (id) setSavedId(id);
    } catch (e) {
      trackEvent('kr_process_error', { process_name: PROCESS_NAME, error_message: e.message || 'save before authentication failed' });
      id = resolveSavedApplicationId(null, savedId);
    }
    if (!id) {
      setSubmitError(SAVE_FAILED_PHONE_MSG);
      setBusy(false); setLoadingServiceId(null);
      return;
    }
    setBankOpen(false);
    if (AUTH_BYPASS) {
      setBusy(false); setLoadingServiceId(null);
      setStep('done');
      setTimeout(scrollToCard, 0);
      return;
    }
    // Invoice funding context (4): the backend identifies the return as laskurahoitus from the "inv" state
    // and queues only the invoice-funding process (not the loan post-authentication credit run).
    setTimeout(() => requestLoginToken(serviceId, AUTH_CONTEXT_INVOICE_FUNDING, id), 500); // redirects the browser
  };

  /* ------------------------------------------------------------ texts */
  const kicker = step === 'done' ? 'Valmis'
    : step === 2 ? 'Vaihe 2 / 2 · Lisätiedot ja tunnistautuminen'
      : prefilled ? 'Vaihe 1 / 2 · Yrityksesi tiedot' : 'Vaihe 1 / 2 · Perustiedot';
  const title = step === 'done' ? '' : step === 2 ? 'Viimeistele avauspyyntö' : 'Laskurahoituksen avaaminen';

  /* ------------------------------------------------------------ render */
  const summary = (
    <div className="kr-loan-summary">
      <div className="kr-ls-head">
        <span className="kr-ls-label">Valitsemasi laskurahoitus</span>
        {prefilled && (
          <button type="button" className="kr-ls-edit" onClick={() => setEditing((v) => !v)}>{editing ? 'Valmis' : 'Muokkaa'}</button>
        )}
      </div>
      <div className="kr-ls-amount">{fmt(fin.volume)} €</div>
      <div className="kr-ls-meta">
        kuukausilaskutus
        <span className="kr-ls-dot">·</span>
        <strong>{formatTerms(fin.terms)}</strong> pv keskimääräinen maksuaika
      </div>

      {editing && <div className="kr-ls-sliders"><FinSliders fin={fin} onChange={changeFin} /></div>}

      <div className="kr-fin-rows">
        <div className="kr-fin-row">
          <div><div className="kr-fr-label">Välitön rahoitus</div><div className="kr-fr-sub">Laskujen koko arvo vähennettynä rahoituskuluilla</div></div>
          <div className="kr-fr-val">{fmt(calc.immediateFunding)} €</div>
        </div>
        <div className="kr-fin-row">
          <div><div className="kr-fr-label">Rahoitusprovisiot kuukaudessa</div><div className="kr-fr-sub">Rahoitusprovisio ~{calc.rateLabel} % maksuajan perusteella</div></div>
          <div className="kr-fr-val">{fmt(calc.monthlyFee)} €</div>
        </div>
        <div className="kr-fin-row">
          <div><div className="kr-fr-label">Kassavirran parantuminen</div><div className="kr-fr-sub">Saat rahat käyttöön {formatTerms(fin.terms)} päivää aiemmin</div></div>
          <div className="kr-fr-val">+{formatTerms(fin.terms)} pv</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section className="kr-hero">
        <div className="kr-container kr-hero-grid">
          <div className="kr-hero-lead">
            <h1>{prefilled ? 'Olet melkein valmis – täytä yrityksesi tiedot' : 'Avaa laskurahoitus ja vapauta sidottu pääoma'}</h1>
            <TrustChips items={CHIPS} />
          </div>

          <div className="kr-form-card" ref={cardRef}>
            <div className="kr-form-head">
              <div className="kr-kicker">{kicker}</div>
              {title && <h2>{title}</h2>}
            </div>
            <StepsBar step={step} labels={['Tiedot', 'Lisätiedot ja tunnistautuminen']} />

            {step === 1 && (
              <div className="kr-form-body" role="group" aria-label="Vaihe 1">
                {summary}
                <p className="kr-calc-note">
                  * Alustava tarjous antamiesi tietojen perusteella; lopulliset ehdot tarkentuvat riskiarviomme perusteella. Luottovakuutus sisältyy hintaan ilman lisäkuluja.
                  Rahoitus perustuu laskutukseesi ja jokainen rahoitettava lasku arvioidaan erikseen.
                </p>
                <hr className="kr-hr" />

                <p className="kr-form-intro">Täytä yrityksesi perustiedot laskurahoituksen avaamiseksi. Voit muuttaa rahoituksen tietoja vielä myöhemmin.</p>

                <CompanyContactFields
                  fields={fields} setField={setField} blurField={blurField} errors={errors} isValid={isValid} bic={bic}
                  extra={(
                    <Field id="kr-description" label="Rahoitettavan liiketoiminnan kuvaus" required error={errors.description} valid={isValid('description')}
                      hint="Kerro lyhyesti, mitä yrityksesi laskuttaa ja keitä asiakkaanne ovat.">
                      <textarea id="kr-description" className="kr-control" rows={3} placeholder="Esim. B2B-konsultointia suomalaisille yrityksille, laskut 30 pv maksuajalla"
                        value={fields.description} onChange={setField('description')} onBlur={blurField('description')} />
                    </Field>
                  )}
                />
                <CampaignCode open={showCampaign} onToggle={() => setShowCampaign((v) => !v)} value={fields.discountCode} onChange={setField('discountCode')} />

                {/* Same two consents as the live invoice financing form. */}
                <Consent id="kr-aiConsent" rowLabel="Hyväksyn automatisoidun päätöksenteon" ariaLabel="Automatisoidun päätöksenteon hyväksyntä"
                  checked={aiConsent} onChange={(v) => { setAiConsent(v); if (v) setErrors((e) => ({ ...e, aiConsent: undefined })); }} error={errors.aiConsent}>
                  Hyväksyn, että luottopäätös tehdään automatisoidusti tekoälyn avulla. Ymmärrän, että minulla on oikeus pyytää ihmisen tekemää arviointia ja vastustaa automatisoidun päätöksenteon käyttöä.
                </Consent>
                <Consent id="kr-terms" checked={terms} onChange={(v) => { setTerms(v); if (v) setErrors((e) => ({ ...e, terms: undefined })); }} error={errors.terms}>
                  Olen tutustunut Kasvurahoituksen <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">käyttöehtoihin</a> ja hyväksyn ne itseäni ja edustamaani yritystä sitoviksi.
                  Annan suostumuksen henkilötietojeni käyttämiseen luottokelpoisuuden arviointiin luottotietorekisteristä tehtäviin kyselyihin sekä muista saatavilla olevista tietolähteistä.
                </Consent>

                <button type="button" className="kr-btn kr-btn-primary" id="kr-toStep2" onClick={goStep2} disabled={busy}>
                  {busy ? <span className="kr-spinner" /> : <>Avaa laskurahoitus<ArrowIcon /></>}
                </button>
                <div className="kr-cta-note"><Tick /> Hakemus n. 2 min · Päätös samana päivänä · Maksuton</div>
                <Assurance items={['Tunnistautuminen vasta seuraavassa vaiheessa.', 'Tietosi käsitellään turvallisesti ja luottamuksellisesti.']} />
              </div>
            )}

            {step === 2 && (
              <div className="kr-form-body" role="group" aria-label="Vaihe 2: Lisätiedot ja tunnistautuminen">
                <p className="kr-auth-intro">
                  Täydennä tiedot ja tunnistaudu. Käsittelemme avauspyynnön ja saat rahoitussopimuksen allekirjoitettavaksi <strong>Visma Signiin</strong>.
                </p>
                <br />
                {AUTH_BYPASS && <div className="kr-notice">Kehitystila: tunnistautuminen ohitetaan, hakemus tallennetaan mock-palvelimelle.</div>}
                {submitError && <div className="kr-form-error" role="alert">{submitError}</div>}

                <Field id="kr-billing" label="Laskutusjärjestelmä" hint="Tämä tieto auttaa meitä yhdistämään laskutuksesi rahoitukseen sujuvasti.">
                  <select id="kr-billing" className="kr-control" value={billingSystem} onChange={(e) => setBillingSystem(parseInt(e.target.value, 10))}>
                    {BILLING_SYSTEMS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>

                <AttachmentsBlock open={showAttachments} onToggle={() => setShowAttachments((v) => !v)} attachments={attachments} onAdd={addFiles} onRemove={removeFile}
                  title="Esimerkkilaskuja, väliaikatilinpäätös tai muu tukidokumentti" />
                <AuthButton busy={busy} onClick={() => setBankOpen(true)} />

                <div className="kr-cta-note"><LockIcon /> Vahva tunnistautuminen, tiedot salataan</div>
                <Assurance items={['Luottovakuutus sisältyy hintaan ilman lisäkuluja.', 'Asiakaspalvelumme auttaa tarvittaessa, ark. 9–16.']} />
                <button type="button" className="kr-back-link" onClick={() => { setStep(1); setTimeout(scrollToCard, 0); }} disabled={busy}>← Takaisin tietoihin</button>
              </div>
            )}

            {step === 'done' && (
              <div className="kr-success" role="status" aria-live="polite">
                <SuccessIcon />
                <h2>Laskurahoitushakemuksesi on rekisteröity ja otettu käsittelyyn</h2>
                {savedId && <p className="kr-success-ref">Hakemusnumero <strong>{savedId}</strong>{fields.companyName ? ` · ${fields.companyName}` : ''} · {fmt(fin.volume)} € / kk</p>}
                <ol className="kr-next-steps">
                  <li>Olemme yhteydessä yhden arkipäivän kuluessa mahdollisissa hakemuksen käsittelyyn liittyvissä kysymyksissä.</li>
                  <li>Saat rahoitussopimuksen allekirjoitettavaksi Visma Signiin sähköpostiin <strong>{fields.email}</strong>.</li>
                </ol>
                <p><strong>Kysyttävää?</strong> Soita <a href="tel:+358968298003">09 6829 8003</a> (ark. 9–16) tai <a href="mailto:laskurahoitus@kasvurahoitus.fi">laskurahoitus@kasvurahoitus.fi</a>.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <BankSelectionPanel open={bankOpen} onClose={() => setBankOpen(false)} onBankSelect={authenticate} disabled={busy} loadingServiceId={loadingServiceId} />

      {step !== 'done' && <StickyCta visible={stickyVisible} label={step === 2 ? 'Viimeistele tunnistautumalla' : 'Siirry hakemukseen'} onClick={scrollToCard} />}
    </>
  );
}

const mapStateToProps = (state) => ({ application: state.application });

const mapDispatchToProps = (dispatch) => ({
  updateApplication: (application) => dispatch(createUpdateApplicant(application)),
  fetchYtj: (bic) => dispatch(getYtjInformation(bic)),
  saveApplication: (application) => dispatch(createSaveApplication(application)),
  requestLoginToken: (serviceId, authContext, applicationId) => dispatch(createLoginToken(serviceId, authContext, applicationId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceFinancingV2Process);
